import type { SessionConfigFiltersType } from "@configs/custom-app-config-schema";
import { isScreenshotMode } from "@constants/common";
import { tagIconUrlMap } from "@constants/mocked-videos";
import { isI18nKeyExists } from "@helpers/common";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { isWeb } from "@helpers/platform";
import { formatCount } from "@memorang/helpers/src/common";
import type { BundleMetaData, Plan } from "@memorang/types/src/bundle";
import type { Distribution } from "@memorang/types/src/content";
import type { Item, MixedItemTag, TagsByType } from "@memorang/types/src/tag";
import { updateUserProperties } from "analytics";
import { t } from "i18next";
import { DateTime } from "luxon";
import type { CurrentView } from "./types";

export const customTitleMap: Record<string, string> = {
	"Major Area": "Knowledge Levels",
	"Diagnostic Level": "Reasoning",
};

export const randomizeScore = () => {
	return Math.floor(Math.random() * 21) + 80;
};

export const isItemArray = (arr: (MixedItemTag | Item)[]): arr is Item[] => {
	return (
		arr.length > 0 &&
		"distribution" in arr[0] &&
		typeof arr[0].distribution === "string"
	);
};

const calculateTagDist = (
	tags: (MixedItemTag | Item)[],
	filterItems: SessionConfigFiltersType,
	showPointsBasedScore?: boolean,
): Distribution => {
	if (isItemArray(tags) && !showPointsBasedScore) {
		return tags.reduce(
			(a, { distribution }) => {
				a[distribution] = a[distribution] ? a[distribution] + 1 : 1;
				return a;
			},
			{
				...Object.assign(
					filterItems.reduce<Distribution>((acc, level) => {
						acc[level.distKey] = 0;
						return acc;
					}, {}),
					{ none: 0 },
				),
			},
		);
	}

	return tags.reduce(
		(acc, tag) => {
			Object.entries(tag.distribution).reduce((_, [key, value]) => {
				acc[key] += value;
				return acc;
			}, acc);
			return acc;
		},
		{
			...Object.assign(
				filterItems.reduce<Distribution>((acc, level) => {
					acc[level.distKey] = 0;
					return acc;
				}, {}),
				{ none: 0 },
			),
		},
	);
};
const getItemsCountDescription = (count: number) => {
	const questions = count;
	// const flashcards = items.filter((item) => item.type === 'flashcard').length;
	const description = [
		...(questions
			? [`${questions} ${formatCount("question", questions)}`]
			: []),
		// ...(flashcards ? [`${flashcards} flashcards`] : []),
	].join(" • ");

	return description;
};
const filterTagsByType = (tagsByType: TagsByType, currentView: CurrentView) => {
	return Object.keys(tagsByType).reduce((a: TagsByType, c) => {
		const currentTags = tagsByType[c];
		const currentTagsWithDist = currentTags.filter(
			(item: MixedItemTag) =>
				item.displayType?.includes(currentView) &&
				(item.numItems || 0 || item.items?.length || 0) > 0,
		);
		a[c] = currentTagsWithDist;
		return a;
	}, {});
};

export const getAllItemsFromTags = (tags: MixedItemTag[]): Item[] => {
	if (!tags?.length) {
		return [];
	}

	return tags.reduce<Item[]>((items, tag) => {
		const tagItems = getAllItemsFromTag(tag);
		for (const item of tagItems) {
			items.push(item);
		}
		return items;
	}, []);
};
export const getAllItemsFromTag = (tag: MixedItemTag): Item[] => {
	let allItems: Item[] = [];

	if (tag.items && tag.items.length > 0) {
		allItems = [...allItems, ...tag.items];
	}

	if (tag.children && tag.children.length > 0) {
		allItems = [...allItems, ...getAllItemsFromTags(tag.children)];
	}

	return allItems;
};
const groupTagsByType = (
	tags: MixedItemTag[],
	currentView: CurrentView,
	freeTagIds: number[],
	hasAccess?: boolean,
	includeCount?: boolean,
) => {
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const tagsByType = tags.reduce((acc: TagsByType, tag) => {
		const tagType = customTitleMap[tag.type] || tag.type;
		if (!acc[tagType]) {
			acc[tagType] = [];
		}
		const dist = tag.distribution;

		const tagLabel = tag.title || tag.label || "";

		const internationalizedTagLabel = isI18nKeyExists(
			`dashboard.skills.${tagLabel}`,
		)
			? t(`dashboard.skills.${tagLabel}`)
			: tagLabel;
		const newTag = {
			...tag,
			iconUrl: tagIconUrlMap[tag.title || tag.label || ""],
			locked: hasAccess ? false : !freeTagIds?.includes(tag.id),
			distribution: isScreenshotMode ? randomizeDistribution(dist) : dist,
			numItems: tag.numItems || tag.items?.length || 0,
			items: getAllItemsFromTag(tag),
			description: getItemsCountDescription(tag.numItems || 0),
			count: tag.numItems || 0,
			type: tag.type,
			title: tag.code
				? `${tag.code} - ${internationalizedTagLabel}`
				: internationalizedTagLabel,
			// score: (
			// 	(dist.high / (dist.medium + dist.low + dist.none + dist.high)) *
			// 	100
			// ).toFixed(),
			score: Math.round(tag.averageScore || 0),
			...(includeCount
				? {
						count: tag.numItems || tag.items?.length || 0,
					}
				: {}),
		};
		acc[tagType].push(newTag);
		return acc;
	}, {});

	const filteredTagsByType = filterTagsByType(tagsByType, currentView);

	return filteredTagsByType;
};

const hasProductAccess = (
	productMetadata?: BundleMetaData,
	productNumericId?: number,
) => {
	if (productMetadata) {
		const {
			subscription: { status, endDate },
		} = productMetadata;
		const isSubExpired = endDate
			? DateTime.now() > DateTime.fromISO(endDate)
			: true;
		const hasAccess = status === "ACTIVE" && !isSubExpired;
		if (productNumericId && hasAccess) {
			updateUserProperties({
				premium_user: "true",
				products: JSON.stringify([
					{
						id: productNumericId,
						type: "study_pack",
					},
				]),
			});
		}
		return isScreenshotMode ? true : hasAccess;
	}
	return false;
};

const filterPlans = (plans: Plan[]) => {
	return plans.filter(
		(plan) =>
			(isWeb && plan.isMobile === false) ||
			(!isWeb && plan.isMobile === true) ||
			plan.isMobile == null,
	);
};

const getNumAttempted = (distribution: Distribution) => {
	return Object.entries(distribution).reduce((acc, [key, value]) => {
		if (key !== "none") {
			return acc + value;
		}
		return acc;
	}, 0);
};
export {
	groupTagsByType,
	hasProductAccess,
	calculateTagDist,
	filterPlans,
	getNumAttempted,
};
